import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, CircleAlert, CircleCheck, Eye, FileUp, FolderOpen, Pause, Play, RotateCcw, X } from "lucide-react";
import { ApiError, driveApi, errorMessage, isAbortError, type DriveItem } from "@/api/driveApi";
import { invalidateDrive } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import { formatBytes, plural } from "@/lib/format";
import { useToast } from "../toast/ToastProvider";
import { useDialogs } from "../dialogs/DialogsProvider";
import { useDriveActions } from "../driveActions";
import { ItemIcon } from "../items/ItemIcon";
import { captureDataTransfer, filesFromFolderInput, walkEntries, type PendingFile } from "./dropUtils";

/** `paused` = resumable, stopped by the user; `needs-file` = restored after a reload, waiting for the file to be re-picked. */
type UploadStatus = "queued" | "uploading" | "paused" | "needs-file" | "done" | "error" | "canceled";

interface UploadTask {
    id: string;
    batchId: string;
    /** Null for a session restored after a page reload until the user picks the file again. */
    file: File | null;
    name: string;
    size: number;
    lastModified: number;
    type: string;
    dirPath: string[];
    status: UploadStatus;
    progress: number;
    error?: string;
    result?: DriveItem;
    /** Resumable chunked upload (files over CHUNKED_THRESHOLD). */
    chunked: boolean;
    /** Server session id once one exists. */
    uploadId?: string;
}

interface Batch {
    rootParentId: string | null;
    /** dirPath "a/b" → created folder id, memoised so each folder is created once. */
    folders: Map<string, Promise<string | null>>;
}

export interface UploadsApi {
    uploadFiles: (files: FileList | File[], parentId: string | null) => void;
    /** Call synchronously from a drop handler; folders keep their tree. */
    uploadDataTransfer: (dt: DataTransfer, parentId: string | null) => void;
    pickFiles: (parentId: string | null) => void;
    pickFolder: (parentId: string | null) => void;
}

const UploadsContext = createContext<UploadsApi | null>(null);

export function useUploads(): UploadsApi {
    const ctx = useContext(UploadsContext);
    if (!ctx) throw new Error("useUploads must be used inside <UploadProvider>");
    return ctx;
}

const MAX_PARALLEL = 3;
/** Files above this go through resumable chunked sessions (POST /api/uploads). */
const CHUNKED_THRESHOLD = 20 * 1024 * 1024;
const DEFAULT_CHUNK = 8 * 1024 * 1024;
/** A failed chunk is retried this many times (1 s, 2 s, 4 s backoff). */
const MAX_CHUNK_RETRIES = 3;
let seq = 0;
const uid = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(seq++).toString(36)}`;
const abortError = () => new DOMException("Upload cancelled", "AbortError");

// ── Resume-after-reload bookkeeping ─────────────────────────────────
// Browsers can't reopen a file on their own, so only the session and enough
// to recognise the file again are kept; the tray asks the user to re-pick it.

const RESUME_KEY = "drive.resumableUploads.v1";

interface SavedUpload {
    uploadId: string;
    name: string;
    size: number;
    lastModified: number;
    parentId: string | null;
}

function isSavedUpload(v: unknown): v is SavedUpload {
    if (!v || typeof v !== "object") return false;
    const s = v as Record<string, unknown>;
    return typeof s.uploadId === "string" && typeof s.name === "string" && typeof s.size === "number"
        && typeof s.lastModified === "number" && (s.parentId === null || typeof s.parentId === "string");
}

function readSaved(): SavedUpload[] {
    try {
        const raw = localStorage.getItem(RESUME_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(isSavedUpload) : [];
    } catch {
        return [];
    }
}

function writeSaved(list: SavedUpload[]) {
    try {
        if (list.length > 0) localStorage.setItem(RESUME_KEY, JSON.stringify(list));
        else localStorage.removeItem(RESUME_KEY);
    } catch {
        /* storage full / disabled — resume-after-reload just won't be offered */
    }
}

const rememberUpload = (s: SavedUpload) => writeSaved([...readSaved().filter((x) => x.uploadId !== s.uploadId), s]);
const forgetUpload = (uploadId: string) => writeSaved(readSaved().filter((x) => x.uploadId !== uploadId));

function sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(abortError());
            return;
        }
        const onAbort = () => {
            window.clearTimeout(timer);
            reject(abortError());
        };
        const timer = window.setTimeout(() => {
            signal.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        signal.addEventListener("abort", onAbort, { once: true });
    });
}

/**
 * Google-Drive-style upload queue: up to 3 files in parallel, per-file
 * progress / cancel / retry, folder trees recreated, and a floating
 * bottom-right tray. Files over 20 MB go up in 8 MB resumable chunks —
 * each chunk retried with backoff, Pause / Resume per file, and sessions
 * still open after a page reload offered for resuming once the file is
 * picked again.
 */
export function UploadProvider({ children }: { children: ReactNode }) {
    const token = useAccessToken();
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const qc = useQueryClient();
    const navigate = useNavigate();
    const toast = useToast();
    const { confirm } = useDialogs();
    const actions = useDriveActions();

    const [tasks, setTasks] = useState<UploadTask[]>([]);
    const tasksRef = useRef(tasks);
    tasksRef.current = tasks;
    const [collapsed, setCollapsed] = useState(false);
    const batches = useRef(new Map<string, Batch>());
    const controllers = useRef(new Map<string, AbortController>());
    const started = useRef(new Set<string>());
    /** Task ids whose abort is a pause, not a cancel. */
    const pauseRequested = useRef(new Set<string>());
    /** Task id → server upload session (set synchronously, unlike task state). */
    const sessionOf = useRef(new Map<string, string>());
    const fileInput = useRef<HTMLInputElement>(null);
    const folderInput = useRef<HTMLInputElement>(null);
    const resumeInput = useRef<HTMLInputElement>(null);
    const pickTarget = useRef<string | null>(null);
    const resumeTarget = useRef<string | null>(null);
    const restored = useRef(false);
    const invalidateTimer = useRef<number | null>(null);

    const update = useCallback((id: string, patch: Partial<UploadTask>) => {
        setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    }, []);

    const scheduleInvalidate = useCallback(() => {
        if (invalidateTimer.current !== null) window.clearTimeout(invalidateTimer.current);
        invalidateTimer.current = window.setTimeout(() => {
            invalidateTimer.current = null;
            void invalidateDrive(qc);
        }, 500);
    }, [qc]);

    const ensureFolder = useCallback((batchId: string, dirPath: string[]): Promise<string | null> => {
        const batch = batches.current.get(batchId);
        if (!batch) return Promise.reject(new Error("Upload batch was cleared"));
        if (dirPath.length === 0) return Promise.resolve(batch.rootParentId);
        const key = dirPath.join("/");
        const cached = batch.folders.get(key);
        if (cached) return cached;
        const p = ensureFolder(batchId, dirPath.slice(0, -1))
            .then((parent) => driveApi.createFolder(parent, dirPath[dirPath.length - 1]!, tokenRef.current))
            .then((f) => f.id);
        // A failed folder shouldn't poison retries.
        p.catch(() => batch.folders.delete(key));
        batch.folders.set(key, p);
        return p;
    }, []);

    /** Drops a task's server session (cancel / clear) — best-effort. */
    const discardSession = useCallback((taskId: string) => {
        const sid = sessionOf.current.get(taskId) ?? tasksRef.current.find((t) => t.id === taskId)?.uploadId;
        sessionOf.current.delete(taskId);
        if (!sid) return;
        forgetUpload(sid);
        driveApi.abortUpload(sid, tokenRef.current).catch(() => undefined);
        update(taskId, { uploadId: undefined });
    }, [update]);

    /** One chunk, retried on network errors / 409 / 429 / 5xx; resolves the committed offset. */
    const sendChunk = useCallback(
        async (uploadId: string, file: File, start: number, end: number, signal: AbortSignal, onLoaded: (bytes: number) => void): Promise<number> => {
            for (let attempt = 0; ; attempt++) {
                try {
                    const res = await driveApi.uploadChunk(uploadId, file.slice(start, end), start, file.size, tokenRef.current, onLoaded, signal);
                    return res.offset;
                } catch (err) {
                    if (isAbortError(err)) throw err;
                    const status = err instanceof ApiError ? err.status : 0;
                    const retryable = status === 0 || status === 401 || status === 408 || status === 409 || status === 429 || status >= 500;
                    if (!retryable || attempt >= MAX_CHUNK_RETRIES) throw err;
                    await sleep(1000 * 2 ** attempt, signal);
                    // Part of the chunk may have landed before the drop — resume from the server's offset.
                    try {
                        const s = await driveApi.getUploadSession(uploadId, tokenRef.current);
                        if (s.offset !== start) return s.offset;
                    } catch (e) {
                        if (e instanceof ApiError && e.status === 404) throw e;
                    }
                }
            }
        },
        []
    );

    const runChunked = useCallback(
        async (task: UploadTask, file: File, signal: AbortSignal, report: (fraction: number) => void): Promise<DriveItem> => {
            let uploadId = sessionOf.current.get(task.id) ?? task.uploadId;
            let offset = 0;
            let chunkSize = DEFAULT_CHUNK;
            if (uploadId) {
                try {
                    const s = await driveApi.getUploadSession(uploadId, tokenRef.current);
                    offset = s.offset;
                    chunkSize = s.chunkSize || DEFAULT_CHUNK;
                } catch (e) {
                    if (!(e instanceof ApiError && e.status === 404)) throw e;
                    // Expired or already gone — start a fresh session.
                    forgetUpload(uploadId);
                    sessionOf.current.delete(task.id);
                    uploadId = undefined;
                }
            }
            if (signal.aborted) throw abortError();
            if (!uploadId) {
                const parentId = await ensureFolder(task.batchId, task.dirPath);
                if (signal.aborted) throw abortError();
                const s = await driveApi.createUploadSession(
                    { name: file.name, size: file.size, contentType: file.type || null, parentId },
                    tokenRef.current
                );
                uploadId = s.uploadId;
                offset = s.offset;
                chunkSize = s.chunkSize || DEFAULT_CHUNK;
                sessionOf.current.set(task.id, uploadId);
                update(task.id, { uploadId });
                rememberUpload({ uploadId, name: file.name, size: file.size, lastModified: file.lastModified, parentId });
            }
            const id = uploadId;
            report(offset / file.size);
            while (offset < file.size) {
                if (signal.aborted) throw abortError();
                const start = offset;
                const end = Math.min(start + chunkSize, file.size);
                offset = await sendChunk(id, file, start, end, signal, (loaded) => report(Math.min(start + loaded, file.size) / file.size));
            }
            const item = await driveApi.completeUpload(id, tokenRef.current);
            forgetUpload(id);
            sessionOf.current.delete(task.id);
            return item;
        },
        [ensureFolder, sendChunk, update]
    );

    const start = useCallback(
        (task: UploadTask) => {
            const file = task.file;
            if (!file) return;
            started.current.add(task.id);
            const ctrl = new AbortController();
            controllers.current.set(task.id, ctrl);
            update(task.id, { status: "uploading", error: undefined });
            let last = -1;
            const report = (f: number) => {
                if (f - last >= 0.01 || f >= 1 || f < last) {
                    last = f;
                    update(task.id, { progress: f });
                }
            };
            void (async () => {
                try {
                    let result: DriveItem;
                    if (task.chunked) {
                        result = await runChunked(task, file, ctrl.signal, report);
                    } else {
                        const parentId = await ensureFolder(task.batchId, task.dirPath);
                        if (ctrl.signal.aborted) throw abortError();
                        result = await driveApi.uploadFile(parentId, file, tokenRef.current, report, ctrl.signal);
                    }
                    update(task.id, { status: "done", progress: 1, result });
                    scheduleInvalidate();
                } catch (err) {
                    if (isAbortError(err)) {
                        if (pauseRequested.current.delete(task.id)) {
                            update(task.id, { status: "paused" });
                        } else {
                            update(task.id, { status: "canceled" });
                            discardSession(task.id);
                        }
                    } else {
                        update(task.id, { status: "error", error: errorMessage(err) });
                    }
                } finally {
                    controllers.current.delete(task.id);
                }
            })();
        },
        [discardSession, ensureFolder, runChunked, scheduleInvalidate, update]
    );

    // Scheduler: keep up to MAX_PARALLEL uploads running.
    useEffect(() => {
        let slots = MAX_PARALLEL - tasks.filter((t) => t.status === "uploading").length;
        for (const t of tasks) {
            if (slots <= 0) break;
            if (t.status === "queued" && t.file && !started.current.has(t.id)) {
                start(t);
                slots--;
            }
        }
    }, [tasks, start]);

    // Sessions left open by an earlier page load: offer to resume them.
    useEffect(() => {
        if (!token || restored.current) return;
        restored.current = true;
        const saved = readSaved();
        if (saved.length === 0) return;
        void (async () => {
            const alive: (SavedUpload & { offset: number })[] = [];
            for (const s of saved) {
                try {
                    const session = await driveApi.getUploadSession(s.uploadId, token);
                    alive.push({ ...s, offset: session.offset });
                } catch (e) {
                    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) forgetUpload(s.uploadId);
                }
            }
            if (alive.length === 0) return;
            const next: UploadTask[] = alive.map((s) => {
                const batchId = uid("batch");
                batches.current.set(batchId, { rootParentId: s.parentId, folders: new Map() });
                return {
                    id: uid("up"),
                    batchId,
                    file: null,
                    name: s.name,
                    size: s.size,
                    lastModified: s.lastModified,
                    type: "",
                    dirPath: [],
                    status: "needs-file",
                    progress: s.size > 0 ? s.offset / s.size : 0,
                    chunked: true,
                    uploadId: s.uploadId
                };
            });
            setTasks((ts) => [...ts, ...next]);
            setCollapsed(false);
        })();
    }, [token]);

    // Don't lose uploads to an accidental tab close.
    const busy = tasks.some((t) => t.status === "queued" || t.status === "uploading");
    useEffect(() => {
        if (!busy) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [busy]);

    const enqueue = useCallback((files: PendingFile[], parentId: string | null) => {
        if (files.length === 0) return;
        const batchId = uid("batch");
        batches.current.set(batchId, { rootParentId: parentId, folders: new Map() });
        const next: UploadTask[] = files.map((f) => ({
            id: uid("up"),
            batchId,
            file: f.file,
            name: f.file.name,
            size: f.file.size,
            lastModified: f.file.lastModified,
            type: f.file.type,
            dirPath: f.dirPath,
            status: "queued",
            progress: 0,
            chunked: f.file.size > CHUNKED_THRESHOLD
        }));
        setTasks((ts) => [...ts, ...next]);
        setCollapsed(false);
    }, []);

    const api = useMemo<UploadsApi>(
        () => ({
            uploadFiles: (files, parentId) => enqueue(Array.from(files).map((file) => ({ file, dirPath: [] })), parentId),
            uploadDataTransfer: (dt, parentId) => {
                const { entries, files } = captureDataTransfer(dt);
                if (entries.some((e) => e.isDirectory)) {
                    walkEntries(entries).then(
                        (list) => enqueue(list, parentId),
                        (e: unknown) => toast.error(e)
                    );
                } else {
                    enqueue(files.map((file) => ({ file, dirPath: [] })), parentId);
                }
            },
            pickFiles: (parentId) => {
                pickTarget.current = parentId;
                fileInput.current?.click();
            },
            pickFolder: (parentId) => {
                pickTarget.current = parentId;
                folderInput.current?.click();
            }
        }),
        [enqueue, toast]
    );

    // React has no typed prop for webkitdirectory — set it on the element.
    useEffect(() => {
        const el = folderInput.current;
        if (!el) return;
        el.setAttribute("webkitdirectory", "");
        el.setAttribute("directory", "");
    }, []);

    const onFilesPicked = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) enqueue(Array.from(e.target.files).map((file) => ({ file, dirPath: [] })), pickTarget.current);
        e.target.value = "";
    };
    const onFolderPicked = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) enqueue(filesFromFolderInput(e.target.files), pickTarget.current);
        e.target.value = "";
    };
    const onResumePicked = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const id = resumeTarget.current;
        resumeTarget.current = null;
        const task = id ? tasksRef.current.find((t) => t.id === id) : undefined;
        if (!file || !task) return;
        if (file.name !== task.name || file.size !== task.size) {
            toast.error(`That isn't “${task.name}” (${formatBytes(task.size)}) — pick the same file to continue.`);
            return;
        }
        if (task.lastModified && file.lastModified !== task.lastModified) {
            toast.error(`“${task.name}” has changed since the upload started — upload it again instead.`);
            return;
        }
        started.current.delete(task.id);
        update(task.id, { file, type: file.type, status: "queued", error: undefined });
    };

    const cancel = (id: string) => {
        pauseRequested.current.delete(id);
        const ctrl = controllers.current.get(id);
        if (ctrl) {
            ctrl.abort(); // the runner marks it cancelled and drops the session
            return;
        }
        update(id, { status: "canceled" });
        discardSession(id);
    };
    const pause = (id: string) => {
        const ctrl = controllers.current.get(id);
        if (!ctrl) return;
        pauseRequested.current.add(id);
        ctrl.abort();
    };
    const resume = (id: string) => {
        started.current.delete(id);
        update(id, { status: "queued", error: undefined });
    };
    const pickToResume = (id: string) => {
        resumeTarget.current = id;
        resumeInput.current?.click();
    };
    const retry = (id: string) => {
        started.current.delete(id);
        update(id, { status: "queued", progress: 0, error: undefined });
    };
    const clearAll = () => {
        for (const t of tasksRef.current) {
            if (t.status !== "done" && (sessionOf.current.has(t.id) || t.uploadId)) discardSession(t.id);
        }
        pauseRequested.current.clear();
        controllers.current.forEach((c) => c.abort());
        controllers.current.clear();
        started.current.clear();
        batches.current.clear();
        sessionOf.current.clear();
        setTasks([]);
    };
    const close = async () => {
        const unfinished = tasks.filter((t) => t.status === "queued" || t.status === "uploading" || t.status === "paused" || t.status === "needs-file").length;
        if (unfinished > 0) {
            const ok = await confirm({
                title: "Cancel uploads?",
                message: `${plural(unfinished, "upload")} haven't finished yet.`,
                confirmLabel: "Cancel uploads",
                cancelLabel: "Keep uploading",
                destructive: true
            });
            if (!ok) return;
        }
        clearAll();
    };

    return (
        <UploadsContext.Provider value={api}>
            {children}
            <input ref={fileInput} type="file" multiple hidden onChange={onFilesPicked} />
            <input ref={folderInput} type="file" multiple hidden onChange={onFolderPicked} />
            <input ref={resumeInput} type="file" hidden onChange={onResumePicked} />
            {tasks.length > 0 ? (
                <UploadTray
                    tasks={tasks}
                    collapsed={collapsed}
                    onToggle={() => setCollapsed((c) => !c)}
                    onClose={() => void close()}
                    onCancel={cancel}
                    onRetry={retry}
                    onPause={pause}
                    onResume={resume}
                    onPickFile={pickToResume}
                    onShow={(item) =>
                        navigate(`${item.parentId ? `/folder/${encodeURIComponent(item.parentId)}` : "/"}?select=${encodeURIComponent(item.id)}`)
                    }
                    onOpen={(item) => actions.open(item)}
                />
            ) : null}
        </UploadsContext.Provider>
    );
}

function UploadTray({
    tasks,
    collapsed,
    onToggle,
    onClose,
    onCancel,
    onRetry,
    onPause,
    onResume,
    onPickFile,
    onShow,
    onOpen
}: {
    tasks: UploadTask[];
    collapsed: boolean;
    onToggle: () => void;
    onClose: () => void;
    onCancel: (id: string) => void;
    onRetry: (id: string) => void;
    onPause: (id: string) => void;
    onResume: (id: string) => void;
    onPickFile: (id: string) => void;
    onShow: (item: DriveItem) => void;
    onOpen: (item: DriveItem) => void;
}) {
    const active = tasks.filter((t) => t.status === "queued" || t.status === "uploading");
    const paused = tasks.filter((t) => t.status === "paused" || t.status === "needs-file").length;
    const done = tasks.filter((t) => t.status === "done").length;
    const failed = tasks.filter((t) => t.status === "error").length;
    const title =
        active.length > 0
            ? `Uploading ${plural(active.length, "item")}`
            : paused > 0
              ? `${plural(paused, "upload")} paused`
              : failed > 0
                ? `${plural(done, "upload")} complete · ${failed} failed`
                : `${plural(done, "upload")} complete`;
    const totalBytes = active.reduce((s, t) => s + t.size, 0);
    const doneBytes = active.reduce((s, t) => s + t.size * t.progress, 0);
    const pct = totalBytes > 0 ? Math.round((doneBytes / totalBytes) * 100) : 0;

    const iconBtn = "flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

    return (
        <section
            aria-label="Uploads"
            className="drive-slide-up fixed bottom-3 right-3 z-[45] w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border bg-card text-card-foreground shadow-2xl sm:bottom-5 sm:right-5"
        >
            <header className="flex h-12 items-center gap-1 bg-muted/70 pl-4 pr-1.5">
                <h2 className="min-w-0 flex-1 truncate text-sm font-semibold" aria-live="polite">{title}</h2>
                {active.length > 0 ? <span className="px-1 text-xs tabular-nums text-muted-foreground">{pct}%</span> : null}
                <button type="button" onClick={onToggle} aria-expanded={!collapsed} aria-label={collapsed ? "Expand uploads" : "Collapse uploads"} className={iconBtn}>
                    {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <button type="button" onClick={onClose} aria-label="Close uploads" className={iconBtn}>
                    <X className="h-4 w-4" />
                </button>
            </header>
            {active.length > 0 ? (
                <div className="h-1 bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Overall upload progress">
                    <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} />
                </div>
            ) : null}
            {!collapsed ? (
                <ul className="max-h-72 overflow-y-auto py-1">
                    {tasks.map((t) => {
                        const where = t.dirPath.length ? t.dirPath.join(" / ") : null;
                        const pctDone = Math.round(t.progress * 100);
                        const subtitle =
                            t.status === "queued" ? (where ? `Waiting · ${where}` : "Waiting…")
                            : t.status === "paused" ? `Paused · ${pctDone}% of ${formatBytes(t.size)}`
                            : t.status === "needs-file" ? `Interrupted at ${pctDone}% — choose the file again to resume`
                            : t.status === "error" ? t.error ?? "Upload failed"
                            : t.status === "canceled" ? "Cancelled"
                            : where ?? formatBytes(t.size);
                        const showBar = t.status === "uploading" || t.status === "paused";
                        return (
                            <li key={t.id} className="group flex items-center gap-3 px-4 py-2">
                                <ItemIcon item={{ kind: "File", name: t.name, contentType: t.type || null }} />
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm" title={[...t.dirPath, t.name].join("/")}>{t.name}</div>
                                    {showBar ? (
                                        <>
                                            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
                                                <div
                                                    className={cn("h-full rounded-full transition-[width] duration-200", t.status === "paused" ? "bg-muted-foreground/50" : "bg-primary")}
                                                    style={{ width: `${pctDone}%` }}
                                                />
                                            </div>
                                            {t.status === "paused" ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div> : null}
                                        </>
                                    ) : (
                                        <div className={cn("truncate text-xs", t.status === "error" ? "text-destructive" : "text-muted-foreground")} title={subtitle}>
                                            {subtitle}
                                        </div>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center">
                                    {t.status === "uploading" || t.status === "queued" ? (
                                        <>
                                            {t.status === "uploading" && t.chunked ? (
                                                <button type="button" onClick={() => onPause(t.id)} aria-label={`Pause upload of ${t.name}`} title="Pause" className={iconBtn}>
                                                    <Pause className="h-4 w-4" />
                                                </button>
                                            ) : null}
                                            <button type="button" onClick={() => onCancel(t.id)} aria-label={`Cancel upload of ${t.name}`} title="Cancel" className={iconBtn}>
                                                <X className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : t.status === "paused" || t.status === "needs-file" ? (
                                        <>
                                            {t.status === "paused" ? (
                                                <button type="button" onClick={() => onResume(t.id)} aria-label={`Resume upload of ${t.name}`} title="Resume" className={iconBtn}>
                                                    <Play className="h-4 w-4" />
                                                </button>
                                            ) : (
                                                <button type="button" onClick={() => onPickFile(t.id)} aria-label={`Choose ${t.name} again to resume`} title="Choose file to resume" className={iconBtn}>
                                                    <FileUp className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button type="button" onClick={() => onCancel(t.id)} aria-label={`Cancel upload of ${t.name}`} title="Cancel" className={iconBtn}>
                                                <X className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : t.status === "done" && t.result ? (
                                        <>
                                            <CircleCheck className="m-1.5 h-5 w-5 text-emerald-500 group-focus-within:hidden group-hover:hidden [@media(hover:none)]:hidden" aria-label="Uploaded" />
                                            <div className="hidden group-focus-within:flex group-hover:flex [@media(hover:none)]:flex">
                                                <button type="button" onClick={() => onShow(t.result!)} aria-label={`Show ${t.name} in folder`} title="Show in folder" className={iconBtn}>
                                                    <FolderOpen className="h-4 w-4" />
                                                </button>
                                                <button type="button" onClick={() => onOpen(t.result!)} aria-label={`Open ${t.name}`} title="Open" className={iconBtn}>
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </>
                                    ) : t.file ? (
                                        <>
                                            {t.status === "error" ? <CircleAlert className="h-4 w-4 text-destructive" aria-hidden /> : null}
                                            <button type="button" onClick={() => onRetry(t.id)} aria-label={`Retry upload of ${t.name}`} title="Retry" className={iconBtn}>
                                                <RotateCcw className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </section>
    );
}
